if (!customElements.get('postcode-checker')) {
  customElements.define(
    'postcode-checker',
    class PostcodeChecker extends HTMLElement {
      constructor() {
        super();

        this.input = this.querySelector('[data-postcode-input]');
        this.resultEl = this.querySelector('[data-postcode-result]');
        this.waitlistEl = this.querySelector('[data-waitlist]');
        this.waitlistForm = this.querySelector('[data-waitlist-form]');
        this.waitlistSuccessEl = this.querySelector('[data-waitlist-success]');
        this.waitlistErrorEl = this.querySelector('[data-waitlist-error]');

        const dataEl = this.querySelector('[data-postcode-config]');
        if (dataEl) {
          try {
            this.config = JSON.parse(dataEl.textContent);
          } catch (e) {
            this.config = {};
          }
        } else {
          this.config = {};
        }

        this.postcodes = new Set(
          (this.config.postcodes || '')
            .split(/[,\s]+/)
            .map((p) => p.trim())
            .filter(Boolean)
        );

        // Pre-fill from saved postcode (persisted across sessions) if set
        const saved = PostcodeChecker.getSavedPostcode();
        if (saved && saved.postcode) {
          this.input.value = saved.postcode;
          if (saved.delivery_eligible) {
            this.showResult('success', this.config.successMessage);
          } else {
            this.showResult('error', this.config.errorMessage);
            this.showWaitlist();
          }
        }

        this.input.addEventListener('input', this.onInput.bind(this));
        if (this.waitlistForm) {
          this.waitlistForm.addEventListener('submit', this.onWaitlistSubmit.bind(this));
        }
      }

      static getSavedPostcode() {
        try {
          // Prefer the persistent localStorage value; fall back to the legacy
          // sessionStorage key so existing visitors aren't re-prompted.
          return (
            JSON.parse(localStorage.getItem('yrja-postcode')) ||
            JSON.parse(sessionStorage.getItem('yrja-postcode'))
          );
        } catch (e) {
          return null;
        }
      }

      static savePostcode(postcode, eligible) {
        // Persist across sessions so the delivery-gate popup does not reappear.
        localStorage.setItem(
          'yrja-postcode',
          JSON.stringify({ postcode: postcode, delivery_eligible: eligible })
        );
      }

      onInput() {
        const value = this.input.value.trim();

        // Reset states
        this.hideResult();
        this.hideWaitlist();

        // Only check when exactly 4 digits
        if (!/^\d{4}$/.test(value)) return;

        if (this.postcodes.has(value)) {
          this.showResult('success', this.config.successMessage);
          PostcodeChecker.savePostcode(value, true);
        } else {
          this.showResult('error', this.config.errorMessage);
          PostcodeChecker.savePostcode(value, false);
          this.showWaitlist();
        }
      }

      showResult(type, message) {
        this.resultEl.textContent = message;
        this.resultEl.className = 'postcode-checker__result is-visible';
        this.resultEl.classList.add(`postcode-checker__result--${type}`);
      }

      hideResult() {
        this.resultEl.classList.remove('is-visible');
        this.resultEl.className = 'postcode-checker__result';
      }

      showWaitlist() {
        if (this.waitlistEl) {
          this.waitlistEl.classList.add('is-visible');
        }
      }

      hideWaitlist() {
        if (this.waitlistEl) {
          this.waitlistEl.classList.remove('is-visible');
          this.waitlistSuccessEl?.classList.remove('is-visible');
          this.waitlistErrorEl?.classList.remove('is-visible');
          if (this.waitlistForm) this.waitlistForm.style.display = '';
        }
      }

      async onWaitlistSubmit(event) {
        event.preventDefault();

        const emailInput = this.waitlistForm.querySelector('[data-waitlist-email]');
        const submitButton = this.waitlistForm.querySelector('button');
        const email = emailInput?.value.trim();
        const postcode = this.input.value.trim();

        if (!email) return;

        const companyId = this.config.klaviyoCompanyId;
        const listId = this.config.klaviyoWaitlistId;

        if (!companyId || !listId) {
          this.showWaitlistError('Venteliste er ikke konfigurert.');
          return;
        }
        this.waitlistErrorEl?.classList.remove('is-visible');

        submitButton.disabled = true;

        try {
          const endpoint = new URL('https://a.klaviyo.com/client/subscriptions/');
          endpoint.searchParams.set('company_id', companyId);

          const response = await fetch(endpoint.toString(), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              revision: '2024-02-15',
            },
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: {
                  custom_source: 'Postcode Checker Waitlist',
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: {
                        email: email,
                        properties: {
                          Postnummer: postcode,
                        },
                      },
                    },
                  },
                },
                relationships: {
                  list: {
                    data: {
                      type: 'list',
                      id: listId,
                    },
                  },
                },
              },
            }),
          });

          if (response.ok || response.status === 202) {
            this.waitlistForm.style.display = 'none';
            this.waitlistSuccessEl?.classList.add('is-visible');
            this.waitlistErrorEl?.classList.remove('is-visible');
          } else {
            let errorMessage = 'Noe gikk galt. Prøv igjen.';
            const responseData = await response.json().catch(() => null);

            if (responseData?.errors?.length) {
              const firstError = responseData.errors[0];
              errorMessage = firstError.detail || firstError.title || errorMessage;
            }

            this.showWaitlistError(errorMessage);
            submitButton.disabled = false;
          }
        } catch (err) {
          this.showWaitlistError('Noe gikk galt. Prøv igjen.');
          submitButton.disabled = false;
        }
      }

      showWaitlistError(message) {
        if (this.waitlistErrorEl) {
          this.waitlistErrorEl.textContent = message;
          this.waitlistErrorEl.classList.add('is-visible');
        }
      }
    }
  );
}
